import { createContext } from 'react';

/**
 * Registry of per-app dialog overrides keyed by dialog id. Each entry is a
 * partial override object; unset fields fall back to the shared defaults.
 * See `docs/modals-theming-refactor.md` → Step 10 for the slot contract and
 * the guiding principles.
 *
 * Slots accept either a static `ReactNode` (for copy-only overrides) or a
 * function component that receives the dialog's runtime props — hooks,
 * Suspense, and surrounding providers all work naturally inside a function
 * slot because it mounts inside the dialog's render context. Use
 * `renderSlot(slot, props)` to dispatch the two shapes uniformly.
 *
 * @typedef {React.ReactNode | React.ComponentType<any>} DialogSlot
 *
 * @typedef {Object} DialogOverride
 * @property {DialogSlot} [title]
 * @property {DialogSlot} [description]
 * @property {DialogSlot} [beforeBody]
 * @property {DialogSlot} [afterBody]
 * @property {DialogSlot[]} [actions]
 * @property {string} [confirmLabel]
 * @property {string} [cancelLabel]
 * @property {(defaults: Object) => React.ReactNode} [renderOverride]
 *
 * @type {React.Context<Record<string, DialogOverride>>}
 */
const DialogOverrideContext = createContext({});

export default DialogOverrideContext;
